/**
 * Contact Us Form Handler
 * Handles form submission and sends emails to both admin and user
 * 
 * Note: Shopify's contact form sends to admin by default.
 * For user confirmation emails, we use Shopify's contact endpoint
 * with a special format that can be processed by Shopify Flow
 * or admin can set up auto-reply.
 */

class ContactUsForm extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.submitButton = null;
    this.checkbox = null;
  }

  connectedCallback() {
    this.form = this.querySelector('#ContactUsForm');
    this.submitButton = this.querySelector('#ContactUs-submit');
    this.checkbox = this.querySelector('#ContactUs-privacy');

    if (!this.form) return;

    this.init();
  }

  init() {
    // Handle terms and conditions checkbox
    if (this.checkbox) {
      const savedCondition = this.getCookie('term_conditions_contact');
      if (savedCondition) {
        this.checkbox.checked = true;
        if (this.submitButton) {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.classList.add('active');
        }
      }

      this.checkbox.addEventListener('change', (e) => {
        if (this.submitButton) {
          if (e.currentTarget.checked) {
            this.submitButton.removeAttribute('disabled');
            this.submitButton.classList.add('active');
            this.setCookie('term_conditions_contact', '1', 1);
          } else {
            this.submitButton.setAttribute('disabled', '');
            this.submitButton.classList.remove('active');
            this.deleteCookie('term_conditions_contact');
          }
        }
      });
    }

    // Handle form submission
    this.form.addEventListener('submit', (e) => {
      this.handleSubmit(e);
    });
  }

  handleSubmit(e) {
    const formData = new FormData(this.form);
    const name = formData.get('contact[name]');
    const email = formData.get('contact[email]');
    const body = formData.get('contact[body]');
    const subject = formData.get('contact[subject]') || 'Contact Us Form Submission';

    // Store form data for confirmation email
    this.formData = { name, email, body, subject };

    if (this.submitButton) {
      this.submitButton.classList.add('loading');
      this.submitButton.setAttribute('aria-disabled', 'true');
      const buttonSpan = this.submitButton.querySelector('.hidden-on-load');
      if (buttonSpan) {
        buttonSpan.classList.add('opacity-0');
      }
      const iconLoad = this.submitButton.querySelector('.icon-load');
      if (iconLoad) {
        iconLoad.classList.remove('opacity-0');
      }
    }

    // After form submits successfully, send confirmation to user
    // Shopify redirects with ?contact_posted=true on success
    this.handlePostSubmit();
  }

  async handlePostSubmit() {
    // Wait for page to potentially reload (Shopify redirects on success)
    // Check if we're on the success state
    await new Promise(resolve => setTimeout(resolve, 1000));

    const urlParams = new URLSearchParams(window.location.search);
    const contactPosted = urlParams.get('contact_posted');
    const formErrors = this.querySelector('.form-message--error');

    if (contactPosted === 'true' || (!formErrors && this.form.querySelector('.form-message--success'))) {
      // Form was successful - send confirmation to user
      if (this.formData) {
        await this.sendUserConfirmation(this.formData);
      }
    }
  }

  async sendUserConfirmation(data) {
    // Send confirmation email copy to user
    // Note: Shopify's contact form only sends to admin by default.
    // For user confirmation, you can:
    // 1. Set up Shopify Flow (paid) to auto-reply to users
    // 2. Use email forwarding rules in your email client
    // 3. Use a third-party service like EmailJS
    
    try {
      const shopName = window.Shop?.name || 
                      document.querySelector('meta[property="og:site_name"]')?.content || 
                      'Customer Service';
      
      const confirmationBody = `Dear ${data.name},\n\n` +
        `Thank you for contacting ${shopName}!\n\n` +
        `We have received your message and will get back to you as soon as possible.\n\n` +
        `---\n` +
        `Your Message:\n` +
        `${data.body}\n` +
        `---\n\n` +
        `If you have any urgent inquiries, please don't hesitate to reach out.\n\n` +
        `Best regards,\n` +
        `${shopName} Team`;

      // Format email data for admin (includes all details in body)
      // Admin can manually forward or set up auto-reply
      const adminFormattedBody = `CONTACT FORM SUBMISSION\n` +
        `========================\n\n` +
        `Name: ${data.name}\n` +
        `Email: ${data.email}\n` +
        `Subject: ${data.subject}\n\n` +
        `Message:\n` +
        `${data.body}\n\n` +
        `---\n\n` +
        `USER CONFIRMATION EMAIL (Forward this to user):\n` +
        `To: ${data.email}\n` +
        `Subject: Re: ${data.subject} - Confirmation\n\n` +
        `${confirmationBody}`;

      // Send a formatted version to admin with user confirmation copy
      const confirmationFormData = new FormData();
      confirmationFormData.append('contact[name]', `${shopName} - Contact Form`);
      confirmationFormData.append('contact[email]', data.email);
      confirmationFormData.append('contact[body]', adminFormattedBody);
      confirmationFormData.append('contact[subject]', `[CONTACT FORM] ${data.subject} - User Email: ${data.email}`);
      confirmationFormData.append('form_type', 'contact');

      // Send via AJAX (this goes to admin, includes formatted user confirmation)
      const response = await fetch('/contact', {
        method: 'POST',
        body: confirmationFormData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (response.ok) {
        return true;
      }
    } catch (error) {
      // Silently fail - main form email was already sent to admin
    }
    return false;
  }

  // Cookie helper functions
  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  }

  getCookie(name) {
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  deleteCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }
}

// Register custom element
if (!customElements.get('contact-us-form')) {
  customElements.define('contact-us-form', ContactUsForm);
}

// Handle successful form submission on page load
document.addEventListener('DOMContentLoaded', () => {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('contact_posted') === 'true') {
    // Form was successfully submitted - scroll to form to show success message
    const formElement = document.querySelector('#ContactUsForm');
    if (formElement) {
      setTimeout(() => {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }

    // Trigger confirmation email after successful submission
    const form = document.querySelector('#ContactUsForm');
    if (form) {
      const formData = {
        name: new URLSearchParams(window.location.search).get('contact[name]') || '',
        email: new URLSearchParams(window.location.search).get('contact[email]') || '',
        body: new URLSearchParams(window.location.search).get('contact[body]') || '',
        subject: new URLSearchParams(window.location.search).get('contact[subject]') || 'Contact Us Form Submission'
      };

      // If we have the form container, trigger confirmation
      const formContainer = form.closest('contact-us-form');
      if (formContainer && formData.email) {
        setTimeout(() => {
          formContainer.sendUserConfirmation(formData);
        }, 500);
      }
    }
  }
});
